/**
 * /settings/campaigns (v10 Etapa 2) — campanhas de WhatsApp: tabela Nome |
 * Canal | Modelo | Status | Progresso | ações (iniciar/pausar/cancelar por
 * status; excluir rascunho), "+ Campanha" abre o wizard e o expand da linha
 * mostra o detalhe (progresso VIVO por status de destinatário), refazendo a
 * cada 5s enquanto a campanha roda. Leitura: integrations.manage; ESCRITAS
 * (criar/iniciar/pausar/cancelar/excluir) são admin-only na API — o front
 * esconde os botões de escrita para não-admin.
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Ban, ChevronDown, Pause, Play, Plus, ShieldAlert, Trash2 } from 'lucide-react';
import type { WaCampaign } from '@erp/shared';
import { useAuthUser } from '@/auth/use-auth';
import { usePermissions } from '@/auth/use-permissions';
import { Button } from '@/components/ui/button';
import { Badge, EmptyState, PageSpinner, Skeleton, Spinner } from '@/components/ui/misc';
import { useTransientToast } from '@/components/ui/transient-toast';
import { cn, formatDateTime } from '@/lib/utils';
import { whatsappChannelsOptions } from '@/features/whatsapp/whatsapp-queries';
import {
  campaignDetailOptions,
  campaignErrorMessage,
  campaignStatusLabel,
  campaignStatusVariant,
  campaignsOptions,
  useCampaignAction,
  useDeleteCampaign,
  type CampaignAction,
} from './campaigns-api';
import { CampaignWizardDialog } from './campaign-wizard';

export function CampaignsPage() {
  const { can, ready } = usePermissions();

  if (!ready) return <PageSpinner />;

  // Mesma regra da API (admin/integrations.manage): sem a permissão, os
  // endpoints negam — renderizar a tela só mostraria erros.
  if (!can('integrations.manage')) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-2 rounded-lg border bg-card text-center shadow-xs">
        <ShieldAlert className="size-8 text-muted-foreground" />
        <p className="text-sm font-medium">Acesso restrito</p>
        <p className="max-w-sm text-sm text-muted-foreground">
          As campanhas de WhatsApp são gerenciadas por quem tem a permissão de integrações. Fale
          com um administrador da sua conta.
        </p>
      </div>
    );
  }

  return <CampaignsAdmin />;
}

function CampaignsAdmin() {
  const [campaignsQ, channelsQ] = useQueries({
    queries: [campaignsOptions(), whatsappChannelsOptions()],
  });
  const [wizardOpen, setWizardOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Escritas são admin-only na API (mesma checagem da IntegrationsPage) —
  // sem admin a lista fica read-only (permission_denied já mapeado no toast).
  const user = useAuthUser();
  const canWrite = user?.role === 'admin';

  const action = useCampaignAction();
  const remove = useDeleteCampaign();
  const { toast: actionToast, showToast: showActionError } = useTransientToast('error', 6000);

  const runAction = (id: number, kind: CampaignAction) => {
    action.mutate(
      { id, action: kind },
      { onError: (e) => showActionError(campaignErrorMessage(e)) }
    );
  };

  const runDelete = (c: WaCampaign) => {
    if (!window.confirm(`Excluir a campanha "${c.name}"? Só rascunhos podem ser excluídos.`)) return;
    remove.mutate(c.id, { onError: (e) => showActionError(campaignErrorMessage(e)) });
  };

  // A campanha traz só o channelId — o número vem da lista de canais (mesma
  // fonte do wizard).
  const channelLabel = (c: WaCampaign): string =>
    (channelsQ.data ?? []).find((ch) => ch.id === c.channelId)?.displayPhone ?? `#${c.channelId}`;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-card shadow-xs">
        <div className="flex flex-wrap items-end justify-between gap-3 px-4 py-3">
          <div>
            <nav className="text-[13px] text-muted-foreground" aria-label="Breadcrumb">
              <Link to="/settings" className="transition-colors hover:text-foreground">
                Configurações
              </Link>
              <span className="mx-1.5">/</span>
              <span>Geral</span>
              <span className="mx-1.5">/</span>
              <span className="text-foreground">Campanhas</span>
            </nav>
            <h1 className="mt-2 text-xl leading-7 font-semibold">Campanhas de WhatsApp</h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Envio em massa de modelos aprovados na Meta, com parâmetros por destinatário.
            </p>
          </div>
          {canWrite ? (
            <Button type="button" size="sm" onClick={() => setWizardOpen(true)}>
              <Plus className="size-4" />
              Campanha
            </Button>
          ) : null}
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border bg-card shadow-xs">
        {campaignsQ.isPending ? (
          <div aria-busy className="space-y-3 p-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : campaignsQ.isError ? (
          <EmptyState
            message={`Erro ao carregar as campanhas: ${campaignErrorMessage(campaignsQ.error)}`}
          />
        ) : campaignsQ.data.length === 0 ? (
          <EmptyState message="Nenhuma campanha ainda — crie a primeira com + Campanha" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted text-left text-[13px]">
                  <th className="h-9 px-4 font-semibold">Nome</th>
                  <th className="h-9 px-4 font-semibold">Canal</th>
                  <th className="h-9 px-4 font-semibold">Modelo</th>
                  <th className="h-9 px-4 font-semibold">Status</th>
                  <th className="h-9 w-48 px-4 font-semibold">Progresso</th>
                  <th className="h-9 w-36 px-4 text-right font-semibold">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {campaignsQ.data.map((c) => (
                  <CampaignRow
                    key={c.id}
                    campaign={c}
                    channelLabel={channelLabel(c)}
                    expanded={expandedId === c.id}
                    onToggle={() => setExpandedId((id) => (id === c.id ? null : c.id))}
                    onAction={runAction}
                    onDelete={runDelete}
                    busy={action.isPending || remove.isPending}
                    canWrite={canWrite}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Toast de erro das ações. */}
      {actionToast}

      {wizardOpen && canWrite ? <CampaignWizardDialog onClose={() => setWizardOpen(false)} /> : null}
    </div>
  );
}

// ── Linha + detalhe ─────────────────────────────────────────────────────────

function CampaignRow({
  campaign: c,
  channelLabel,
  expanded,
  onToggle,
  onAction,
  onDelete,
  busy,
  canWrite,
}: {
  campaign: WaCampaign;
  channelLabel: string;
  expanded: boolean;
  onToggle: () => void;
  onAction: (id: number, action: CampaignAction) => void;
  onDelete: (c: WaCampaign) => void;
  busy: boolean;
  canWrite: boolean;
}) {
  const total = c.totalRecipients;
  const pct = total > 0 ? Math.min(100, Math.round((c.sentCount / total) * 100)) : 0;

  return (
    <>
      <tr className="group border-b border-divider transition-colors last:border-0 hover:bg-accent/60">
        <td className="max-w-56 truncate px-4 py-2.5 font-medium" title={c.name}>
          {c.name}
        </td>
        <td className="px-4 py-2.5 whitespace-nowrap tabular-nums">{channelLabel}</td>
        <td className="max-w-48 truncate px-4 py-2.5 text-muted-foreground" title={c.templateName}>
          {c.templateName}
          <span className="text-xs"> · {c.templateLanguage}</span>
        </td>
        <td className="px-4 py-2.5 whitespace-nowrap">
          <Badge variant={campaignStatusVariant(c.status)}>{campaignStatusLabel(c.status)}</Badge>
        </td>
        <td className="px-4 py-2.5">
          <div className="flex items-center gap-2 whitespace-nowrap">
            <span className="text-xs tabular-nums">
              {c.sentCount}/{total}
            </span>
            <div
              className="h-1.5 w-20 overflow-hidden rounded-full bg-muted"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={total}
              aria-valuenow={c.sentCount}
              aria-label={`Progresso da campanha ${c.name}`}
            >
              <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
          {c.deniedCount > 0 || c.failedCount > 0 ? (
            <p className="mt-0.5 text-[11px] text-muted-foreground tabular-nums">
              {c.deniedCount > 0 ? `${c.deniedCount} negados` : null}
              {c.deniedCount > 0 && c.failedCount > 0 ? ' · ' : null}
              {c.failedCount > 0 ? `${c.failedCount} falhas` : null}
            </p>
          ) : null}
        </td>
        <td className="px-4 py-2">
          <div className="flex justify-end gap-1">
            {canWrite && (c.status === 'draft' || c.status === 'paused') ? (
              <Button
                variant="ghost"
                size="icon-xs"
                title={c.status === 'paused' ? 'Retomar' : 'Iniciar'}
                aria-label={`${c.status === 'paused' ? 'Retomar' : 'Iniciar'} a campanha ${c.name}`}
                className="text-success hover:text-success"
                disabled={busy}
                onClick={() => onAction(c.id, 'start')}
              >
                <Play />
              </Button>
            ) : null}
            {canWrite && c.status === 'running' ? (
              <Button
                variant="ghost"
                size="icon-xs"
                title="Pausar"
                aria-label={`Pausar a campanha ${c.name}`}
                disabled={busy}
                onClick={() => onAction(c.id, 'pause')}
              >
                <Pause />
              </Button>
            ) : null}
            {canWrite && (c.status === 'running' || c.status === 'paused') ? (
              <Button
                variant="ghost"
                size="icon-xs"
                title="Cancelar"
                aria-label={`Cancelar a campanha ${c.name}`}
                className="text-destructive hover:bg-destructive/5 hover:text-destructive"
                disabled={busy}
                onClick={() => onAction(c.id, 'cancel')}
              >
                <Ban />
              </Button>
            ) : null}
            {canWrite && c.status === 'draft' ? (
              <Button
                variant="ghost"
                size="icon-xs"
                title="Excluir rascunho"
                aria-label={`Excluir a campanha ${c.name}`}
                className="text-destructive hover:bg-destructive/5 hover:text-destructive"
                disabled={busy}
                onClick={() => onDelete(c)}
              >
                <Trash2 />
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="icon-xs"
              title="Detalhes"
              aria-label={`Detalhes da campanha ${c.name}`}
              aria-expanded={expanded}
              onClick={onToggle}
            >
              <ChevronDown className={cn('transition-transform', expanded && 'rotate-180')} />
            </Button>
          </div>
        </td>
      </tr>
      {expanded ? (
        <tr>
          <td colSpan={6} className="border-b border-divider bg-background/60 px-4 py-4 last:border-0">
            <CampaignDetail id={c.id} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

/**
 * Detalhe expandido: progresso VIVO por status de destinatário (o GET /:id
 * agrega sobre os destinatários — a API não devolve a lista individual).
 */
function CampaignDetail({ id }: { id: number }) {
  const detailQ = useQuery(campaignDetailOptions(id));

  if (detailQ.isPending) {
    return (
      <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground" aria-busy>
        <Spinner className="size-4" /> Carregando detalhes…
      </div>
    );
  }
  if (detailQ.isError) {
    return (
      <p className="text-sm text-destructive" role="alert">
        Erro ao carregar o detalhe: {campaignErrorMessage(detailQ.error)}
      </p>
    );
  }

  const d = detailQ.data;
  const p = d.progress;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
        <DetailStat label="Total" value={d.totalRecipients} />
        <DetailStat label="Pendentes" value={p.pending} />
        <DetailStat label="Enviados" value={p.sent} />
        <DetailStat label="Entregues" value={p.delivered} />
        <DetailStat label="Lidos" value={p.read} />
        <DetailStat label="Negados" value={p.denied} muted={p.denied === 0} />
        <DetailStat label="Falhas" value={p.failed} muted={p.failed === 0} />
      </div>
      <div className="flex flex-wrap gap-x-8 gap-y-1 text-xs text-muted-foreground">
        <span>Criada em {formatDateTime(d.createdAt)}</span>
        {d.startedAt ? <span>Iniciada em {formatDateTime(d.startedAt)}</span> : null}
        {d.finishedAt ? <span>Encerrada em {formatDateTime(d.finishedAt)}</span> : null}
      </div>
    </div>
  );
}

function DetailStat({ label, value, muted }: { label: string; value: number; muted?: boolean }) {
  return (
    <div className={cn(muted && 'opacity-60')}>
      <div className="text-[13px] text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-semibold tabular-nums">{value}</div>
    </div>
  );
}
